import { useCallback, useRef } from 'react';
import { type NavigateOptions, useSearchParams } from 'react-router';

import { createCustomSearchParamsUpdater } from './createCustomSearchParamsUpdater';

export type Setter<T extends object> = (nextInit: T | ((prev: T) => T), navigateOpts?: NavigateOptions) => void;

export function createSearchParamsHook<T extends object>({
  parse,
  serialize,
  navigateOpts,
}: {
  parse: (searchParams: URLSearchParams) => T;
  serialize: (t: T) => URLSearchParams;
  navigateOpts?: NavigateOptions | undefined;
}) {
  return function useSearchParamsInner(defaultInit?: T) {
    const [searchParams, setSearchParams] = useSearchParams(
      defaultInit !== undefined ? serialize(defaultInit) : undefined,
    );

    // Some consumers re-invoke this factory on every render with fresh,
    // context-specific `parse`/`serialize` closures (e.g. a tab-aware hook whose
    // rules depend on the active tab). Keep the latest inputs in a ref so the
    // memoized callbacks below can stay referentially stable — avoiding the
    // dependency-array footgun — while always resolving against the current
    // parse/serialize rules and search params rather than a stale first-render
    // snapshot.
    const latest = useRef({ parse, serialize, navigateOpts, searchParams });
    latest.current = { parse, serialize, navigateOpts, searchParams };

    const createSearchParams = useCallback((init: T | ((prev: T) => T)) => {
      const updater = createCustomSearchParamsUpdater({
        parse: latest.current.parse,
        serialize: latest.current.serialize,
      });
      return updater(latest.current.searchParams, init);
    }, []);

    const setCustomSearchParams = useCallback(
      (init: T | ((prev: T) => T)) => {
        setSearchParams(createSearchParams(init), latest.current.navigateOpts);
      },
      [createSearchParams, setSearchParams],
    );

    return [parse(searchParams), setCustomSearchParams, createSearchParams] as const;
  };
}
